import { useNavigate } from 'react-router'
import { getUserRoles } from 'src/mentor/layout/utils'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { _supportNav } from 'src/supportPortal/navigation/_nav'
import {
  SupportHomePageContainer,
  SupportHomePageItem,
  TileIconContainer,
  TileText,
  TILE_COLORS,
} from './styledComponents'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Assignment from '@mui/icons-material/Assignment'
import People from '@mui/icons-material/People'
import CalendarToday from '@mui/icons-material/CalendarToday'
import Event from '@mui/icons-material/Event'
import Psychology from '@mui/icons-material/Psychology'
import PersonSearch from '@mui/icons-material/PersonSearch'
import GroupIcon from '@mui/icons-material/Group'
import LocalHospital from '@mui/icons-material/LocalHospital'
import PersonAddAlt1 from '@mui/icons-material/PersonAddAlt1'
import Receipt from '@mui/icons-material/Receipt'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Security from '@mui/icons-material/Security'
import Business from '@mui/icons-material/Business'
import Description from '@mui/icons-material/Description'
import MedicalServices from '@mui/icons-material/MedicalServices'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import ViewWeek from '@mui/icons-material/ViewWeek'
import Insights from '@mui/icons-material/Insights'
import Payment from '@mui/icons-material/Payment'
import Campaign from '@mui/icons-material/Campaign'
import AccountTree from '@mui/icons-material/AccountTree'
import PauseCircleOutline from '@mui/icons-material/PauseCircleOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import useServerAuth from 'src/customHooks/useServerAuth'
import {
  canViewSupportBookings,
  canViewSupportCustomCommunications,
  canViewSupportPaymentLinks,
  canViewSupportVcAuditBookings,
} from 'src/supportPortal/access/accessMatrix'

// Helper function to extract a lighter background color from gradient
const getBackgroundColorFromGradient = (gradient) => {
  if (!gradient) return 'rgba(249, 250, 251, 0.95)'
  
  // Extract first color from gradient (most common pattern: rgb(...) or rgba(...))
  const colorMatch = gradient.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/)
  if (colorMatch) {
    const r = parseInt(colorMatch[1])
    const g = parseInt(colorMatch[2])
    const b = parseInt(colorMatch[3])
    // Create a very light version of the color (90% white + 10% original color)
    const lightR = Math.round(r * 0.1 + 255 * 0.9)
    const lightG = Math.round(g * 0.1 + 255 * 0.9)
    const lightB = Math.round(b * 0.1 + 255 * 0.9)
    return `rgba(${lightR}, ${lightG}, ${lightB}, 0.95)`
  }
  
  // Fallback colors for specific gradients
  if (gradient.includes('rgb(216, 101, 0)')) return 'rgba(255, 245, 235, 0.95)' // Orange tint
  if (gradient.includes('rgb(17, 39, 71)') || gradient.includes('rgb(198, 82, 67)')) return 'rgba(250, 245, 245, 0.95)' // Red/Blue tint
  if (gradient.includes('rgb(0, 152, 186)')) return 'rgba(235, 250, 255, 0.95)' // Cyan tint
  if (gradient.includes('rgb(68, 25, 199)') || gradient.includes('rgb(104, 78, 220)')) return 'rgba(245, 240, 255, 0.95)' // Purple tint
  if (gradient.includes('rgb(34, 193, 195)') || gradient.includes('rgb(253, 187, 45)')) return 'rgba(255, 252, 240, 0.95)' // Yellow/Cyan tint
  if (gradient.includes('rgb(34, 139, 34)') || gradient.includes('rgb(0, 128, 0)')) return 'rgba(240, 255, 240, 0.95)' // Green tint
  
  return 'rgba(249, 250, 251, 0.95)'
}

// Helper function to get subpage tiles for each navigation item
const getSubpageTiles = (navId, allRolesInBoolean) => {
  const tiles = []

  switch (navId) {
    case 'patient-management':
      tiles.push({
        id: 'therapy-actions',
        name: 'Therapy Actions',
        icon: Assignment,
        color: TILE_COLORS.therapyActions,
        path: '/support-home/patient-management/therapy-actions',
        visible: true,
      })
      if (
        allRolesInBoolean.isL2TherapySupport ||
        allRolesInBoolean.isL3TherapySupport ||
        allRolesInBoolean.isClinicSuccessManager ||
        allRolesInBoolean.isClinicSuccessAdmin ||
        allRolesInBoolean.isAdmin
      ) {
        tiles.push({
          id: 'care-orchestration',
          name: 'Care Orchestration',
          icon: AccountTree,
          color: TILE_COLORS.customCommunications,
          path: '/support-home/patient-management/care-orchestration',
          visible: true,
        })
      }
      if (
        allRolesInBoolean.isL2TherapySupport ||
        allRolesInBoolean.isL3TherapySupport ||
        allRolesInBoolean.isClinicSuccessManager
      ) {
        tiles.push({
          id: 'patient-list',
          name: 'Patient List',
          icon: People,
          color: TILE_COLORS.patientList,
          path: '/support-home/patient-management/list',
          visible: true,
        })
      }
      if (
        allRolesInBoolean.isL1TherapySupport ||
        allRolesInBoolean.isL2TherapySupport ||
        allRolesInBoolean.isL3TherapySupport
      ) {
        tiles.push({
          id: 'therapy-scheduled',
          name: 'Therapy Scheduled Patients',
          icon: CalendarToday,
          color: TILE_COLORS.therapyScheduled,
          path: '/support-home/patient-management/therapy-scheduled-patients',
          visible: true,
        })
        tiles.push({
          id: 'consultation-scheduled',
          name: 'Consultation Scheduled Patients',
          icon: Event,
          color: TILE_COLORS.consultationScheduled,
          path: '/support-home/patient-management/consultation-scheduled-patients',
          visible: true,
        })
      }
      break

    case 'practitioner-management':
      if (
        allRolesInBoolean.isTherapistAdmin ||
        allRolesInBoolean.isClinicSuccessAdmin ||
        allRolesInBoolean.isAdmin
      ) {
        tiles.push({
          id: 'practitioner-onboarding',
          name: 'Practitioner Onboarding',
          icon: PersonAddAlt1,
          color: TILE_COLORS.practitionerOnboarding,
          path: '/support-home/practitioner-management/onboarding',
          visible: true,
        })
      }
      if (
        allRolesInBoolean.isL1TherapySupport ||
        allRolesInBoolean.isL2TherapySupport ||
        allRolesInBoolean.isL3TherapySupport ||
        allRolesInBoolean.isTherapistAdmin
      ) {
        tiles.push({
          id: 'therapists',
          name: 'Therapists',
          icon: Psychology,
          color: TILE_COLORS.therapists,
          path: '/support-home/practitioner-management/therapists',
          visible: true,
        })
        // Phase 10 — Applicants and Screeners lifted out of the Therapists tabs into their own
        // tiles so each surface stays focused. Same role gate as Therapists.
        tiles.push({
          id: 'applicants',
          name: 'Applicants',
          icon: PersonSearch,
          color: TILE_COLORS.applicants,
          path: '/support-home/practitioner-management/applicants',
          visible: true,
        })
        // Phase 14m — Disabled Practitioners tile lifted up to the home page so the support
        // team can find practitioners that landed via "Save & Keep Disabled" without
        // navigating into the Practitioner Management nav page first.
        tiles.push({
          id: 'disabled-practitioners',
          name: 'Disabled Practitioners',
          icon: PauseCircleOutline,
          color: TILE_COLORS.disabledPractitioners,
          path: '/support-home/practitioner-management/disabled-practitioners',
          visible: true,
        })
      }
      if (
        allRolesInBoolean.isL1TherapySupport ||
        allRolesInBoolean.isL2TherapySupport ||
        allRolesInBoolean.isL3TherapySupport ||
        allRolesInBoolean.isTherapistAdmin ||
        allRolesInBoolean.isScreenerAdmin
      ) {
        tiles.push({
          id: 'screeners',
          name: 'Screeners',
          icon: GroupIcon,
          color: TILE_COLORS.screeners,
          path: '/support-home/practitioner-management/screeners',
          visible: true,
        })
      }
      if (allRolesInBoolean.isClinicSuccessManager || allRolesInBoolean.isClinicSuccessAdmin) {
        tiles.push({
          id: 'doctors',
          name: 'Doctors',
          icon: LocalHospital,
          color: TILE_COLORS.doctors,
          path: '/support-home/practitioner-management/doctors',
          visible: true,
        })
      }
      if (allRolesInBoolean.isL3TherapySupport) {
        tiles.push({
          id: 'payout-receipts',
          name: 'Payout Receipts',
          icon: Receipt,
          color: TILE_COLORS.payoutReceipts,
          path: '/support-home/practitioner-management/payout-receipts',
          visible: true,
        })
      }
      if (
        allRolesInBoolean.isL2TherapySupport ||
        allRolesInBoolean.isL3TherapySupport ||
        allRolesInBoolean.isClinicSuccessAdmin
      ) {
        tiles.push({
          id: 'service-approvals',
          name: 'Service Approvals',
          icon: CheckCircle,
          color: TILE_COLORS.serviceApprovals,
          path: '/support-home/practitioner-management/service-approvals',
          visible: true,
        })
      }
      if (
        allRolesInBoolean.isL3TherapySupport ||
        allRolesInBoolean.isClinicSuccessAdmin ||
        allRolesInBoolean.isClinicSuccessManager
      ) {
        tiles.push({
          id: 'audit-requests',
          name: 'Audit Requests',
          icon: Security,
          color: TILE_COLORS.serviceApprovals,
          path: '/support-home/practitioner-management/auditors/audit',
          visible: true,
        })
      }
      break

    case 'clinics-hospitals':
      if (allRolesInBoolean.isClinicSuccessManager || allRolesInBoolean.isClinicSuccessAdmin) {
        tiles.push({
          id: 'clinics',
          name: 'Clinics',
          icon: Business,
          color: TILE_COLORS.clinics,
          path: '/support-home/clinics-hospitals/clinics',
          visible: true,
        })
      }
      if (allRolesInBoolean.isClinicRosterManager) {
        tiles.push({
          id: 'clinic-roster',
          name: 'Clinic Roster',
          icon: CalendarToday,
          color: TILE_COLORS.clinicRoster,
          path: '/support-home/clinics-hospitals/clinic-roster',
          visible: true,
        })
      }
      if (allRolesInBoolean.isClinicSuccessManager || allRolesInBoolean.isClinicSuccessAdmin) {
        tiles.push({
          id: 'clinic-brand-invoices',
          name: 'Clinic Brand Invoices',
          icon: Description,
          color: TILE_COLORS.clinicBrandInvoices,
          path: '/support-home/clinics-hospitals/clinic-brand-invoices',
          visible: true,
        })
      }
      break

    case 'management-reporting':
      if (allRolesInBoolean.isManagement) {
        tiles.push({
          id: 'management-reporting-bookings',
          name: 'Bookings',
          icon: Insights,
          color: 'linear-gradient(46deg, rgb(18, 93, 152) 0%, rgb(31, 143, 110) 100%)',
          path: '/support-home/management-reporting/bookings',
          visible: true,
        })
      }
      break

    default:
      break
  }

  return tiles.filter((tile) => tile.visible)
}

// Helper function to get important actions tiles
const getImportantActionsTiles = (allRolesInBoolean, userRoles = []) => {
  const tiles = []

  // Interventions Requested
  if (
    allRolesInBoolean.isL2TherapySupport ||
    allRolesInBoolean.isL3TherapySupport ||
    allRolesInBoolean.isClinicSuccessManager ||
    allRolesInBoolean.isClinicSuccessAdmin
  ) {
    tiles.push({
      id: 'interventions-requested',
      name: 'Interventions Requested',
      icon: MedicalServices,
      color: TILE_COLORS.interventionsRequested,
      path: '/support-home/prescription-interventions',
      visible: true,
    })
  }

  // Bookings & Appointments - role gated by support booking access and/or VC audit access
  if (
    canViewSupportBookings(userRoles) ||
    canViewSupportVcAuditBookings(userRoles)
  ) {
    tiles.push({
      id: 'bookings-appointments',
      name: 'Bookings & Appointments',
      icon: CalendarMonth,
      color: TILE_COLORS.bookingsAppointments,
      path: '/support-home/booked-events',
      visible: true,
    })
  }

  if (canViewSupportPaymentLinks(userRoles)) {
    tiles.push({
      id: 'payment-links',
      name: 'Payment Links',
      icon: Payment,
      color: TILE_COLORS.paymentLinks,
      path: '/support-home/payment-links',
      visible: true,
    })
  }

  // Overall Roster - for Roster Managers and Admins
  if (allRolesInBoolean.isClinicRosterManager || allRolesInBoolean.isClinicRosterAdmin) {
    tiles.push({
      id: 'overall-roster',
      name: 'Overall Roster',
      icon: ViewWeek,
      color: TILE_COLORS.overallRoster,
      path: '/support-home/roster-management/overall-roster',
      visible: true,
    })
  }

  if (allRolesInBoolean.isManagement) {
    tiles.push({
      id: 'custom-communications',
      name: 'Custom Communications',
      icon: Campaign,
      color: TILE_COLORS.customCommunications,
      path: '/support-home/important-actions/custom-communications',
      visible: true,
    })
  }

  if (canViewSupportCustomCommunications(userRoles)) {
    tiles.push({
      id: 'management-reporting',
      name: 'Management Reporting',
      icon: Insights,
      color: 'linear-gradient(46deg, rgb(18, 93, 152) 0%, rgb(31, 143, 110) 100%)',
      path: '/support-home/management-reporting/bookings',
      visible: true,
    })
  }

  // DocSide - only for DocSide admins
  if (allRolesInBoolean.isDocSideAdmin) {
    tiles.push({
      id: 'doc-side',
      name: 'DocSide',
      icon: Description,
      color: TILE_COLORS.docSide,
      path: '/support-home/doc-side',
      visible: true,
    })
  }

  return tiles.filter((tile) => tile.visible)
}

export default function SupportHomePage() {
  const navigate = useNavigate()
  const userRoles = getUserRoles()
  const { allRolesInBoolean } = useServerAuth()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  // Guard so page renders even while roles are loading
  const roles = allRolesInBoolean ?? {}

  const navs = _supportNav(userRoles, true)
  
  // Filter out important actions from main nav (booked-events, doc-side, and overall-roster)
  const importantActionIds = ['booked-events', 'doc-side', 'overall-roster', 'management-reporting']
  const filteredNavs = navs.filter((nav) => !importantActionIds.includes(nav.id))
  
  // Sort navigation items: practitioner-management first, then alphabetical
  const sortedNavs = [...filteredNavs].sort((a, b) => {
    if (a.id === 'practitioner-management') return -1
    if (b.id === 'practitioner-management') return 1
    return a.name.localeCompare(b.name)
  })

  // Get important actions tiles
  const importantActionsTiles = getImportantActionsTiles(roles, userRoles)

  return (
    <>
      <SupportHeader heading="Welcome" />
      <Box
        sx={{
          width: '100%',
          padding: { xs: '24px 20px', sm: '28px 24px', md: '32px 28px' },
          background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
        }}
      >
        {/* Important Actions Section */}
        {importantActionsTiles.length > 0 && (
          <Accordion
            defaultExpanded={isDesktop}
            sx={{
              marginBottom: '18px',
              borderRadius: '12px !important',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08) !important',
              '&:before': {
                display: 'none',
              },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#FFFFFF' }} />}
              sx={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)',
                color: '#FFFFFF',
                borderRadius: '12px',
                minHeight: '80px !important',
                '&.Mui-expanded': {
                  minHeight: '80px !important',
                  borderRadius: '12px 12px 0 0',
                },
                '& .MuiAccordionSummary-content': {
                  margin: '12px 16px',
                  alignItems: 'center',
                  gap: '14px',
                },
              }}
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              <TileIconContainer>
                <Assignment />
              </TileIconContainer>
              <TileText>Important Actions</TileText>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: { xs: '18px 20px', sm: '20px 24px', md: '22px 28px' },
                background: 'rgba(235, 250, 255, 0.95)',
              }}
            >
              <SupportHomePageContainer
                sx={{
                  padding: 0,
                  background: 'transparent',
                  gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(180px, 1fr))', sm: 'repeat(auto-fill, minmax(200px, 1fr))', md: 'repeat(auto-fill, minmax(220px, 1fr))' },
                  gap: { xs: '16px', sm: '18px', md: '20px' },
                }}
              >
                {importantActionsTiles.map((tile) => {
                  const TileIcon = tile.icon
                  return (
                    <SupportHomePageItem
                      key={tile.id}
                      otherColor={tile.color}
                      onClick={() => navigate(tile.path)}
                    >
                      <TileIconContainer>
                        <TileIcon />
                      </TileIconContainer>
                      <TileText>{tile.name}</TileText>
                    </SupportHomePageItem>
                  )
                })}
              </SupportHomePageContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {sortedNavs?.map((navItem) => {
          const IconComponent = navItem?.icon
          const subpageTiles = getSubpageTiles(navItem?.id, roles)

          // If there are no subpage tiles, render as a regular tile (non-accordion)
          if (subpageTiles.length === 0) {
          return (
              <Box key={navItem?.id} sx={{ marginBottom: '18px' }}>
            <SupportHomePageItem
              otherColor={navItem?.color}
              onClick={() => navigate(navItem?.to)}
            >
              {IconComponent && (
                <TileIconContainer>
                  <IconComponent />
                </TileIconContainer>
              )}
              <TileText>{navItem?.name}</TileText>
            </SupportHomePageItem>
              </Box>
            )
          }

          // Render as accordion with subpage tiles
          return (
            <Accordion
              key={navItem?.id}
              defaultExpanded={isDesktop}
              sx={{
                marginBottom: '18px',
                borderRadius: '12px !important',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08) !important',
                '&:before': {
                  display: 'none',
                },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#FFFFFF' }} />}
                sx={{
                  background: navItem?.color || 'linear-gradient(135deg, rgba(139, 92, 246, 0.95) 0%, rgba(124, 58, 237, 0.95) 50%, rgba(109, 40, 217, 0.95) 100%)',
                  color: '#FFFFFF',
                  borderRadius: '12px',
                  minHeight: '80px !important',
                  '&.Mui-expanded': {
                    minHeight: '80px !important',
                    borderRadius: '12px 12px 0 0',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '12px 16px',
                    alignItems: 'center',
                    gap: '14px',
                  },
                }}
                onClick={(e) => {
                  // Prevent navigation when clicking on accordion header
                  e.stopPropagation()
                }}
              >
                {IconComponent && (
                  <TileIconContainer>
                    <IconComponent />
                  </TileIconContainer>
                )}
                <TileText>{navItem?.name}</TileText>
                {/* Phase 14m — direct link to the section's nav page (e.g. /support-home/
                    practitioner-management). _supportNav() already rewrites navItem.to to be
                    the absolute path under /support-home, so we hand it straight to navigate;
                    earlier we double-prefixed it and produced /support-home/support-home/…. */}
                {navItem?.to && (
                  <Box
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(navItem.to)
                    }}
                    sx={{
                      ml: 'auto',
                      mr: 1,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '999px',
                      background: 'rgba(255,255,255,0.18)',
                      color: '#FFFFFF',
                      border: '1px solid rgba(255,255,255,0.45)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      transition: 'background 0.15s ease',
                      '&:hover': { background: 'rgba(255,255,255,0.32)' },
                      '&:focus-visible': {
                        outline: '2px solid rgba(255,255,255,0.85)',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    Open page
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Box>
                )}
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: { xs: '18px 20px', sm: '20px 24px', md: '22px 28px' },
                  background: getBackgroundColorFromGradient(navItem?.color),
                }}
              >
                <SupportHomePageContainer
                  sx={{
                    padding: 0,
                    background: 'transparent',
                    gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(180px, 1fr))', sm: 'repeat(auto-fill, minmax(200px, 1fr))', md: 'repeat(auto-fill, minmax(220px, 1fr))' },
                    gap: { xs: '16px', sm: '18px', md: '20px' },
                  }}
                >
                  {subpageTiles.map((tile) => {
                    const TileIcon = tile.icon
                    return (
                      <SupportHomePageItem
                        key={tile.id}
                        otherColor={tile.color}
                        onClick={() => navigate(tile.path)}
                      >
                        <TileIconContainer>
                          <TileIcon />
                        </TileIconContainer>
                        <TileText>{tile.name}</TileText>
                      </SupportHomePageItem>
                    )
                  })}
                </SupportHomePageContainer>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    </>
  )
}
